import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Download } from "lucide-react";
import { api } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/Seo";
import { toast } from "@/components/ui/sonner";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (!user) return;
    api.get("/downloads/me").then((response) => setOrders(response.data)).finally(() => setFetching(false));
  }, [user]);

  async function resendEmail(orderId) {
    try {
      await api.post(`/orders/${orderId}/resend-download-email`);
      toast.success("Download links emailed again");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Email could not be sent");
    }
  }

  if (loading) return <div data-testid="dashboard-loading-state" className="min-h-screen bg-onyx pt-40 text-center font-mono text-xs uppercase tracking-[0.3em] text-gold">Loading account</div>;
  if (!user) return <Navigate to="/account" replace />;

  return (
    <div data-testid="customer-dashboard-downloads-tab" className="min-h-screen bg-onyx px-4 pt-32 sm:px-8 lg:px-12">
      <Seo title="Downloads Dashboard" description="Access your purchased mellowjmedia high-resolution photography downloads." />
      <div className="mx-auto max-w-6xl pb-24">
        <p className="font-mono text-xs uppercase tracking-[0.42em] text-gold">Collector dashboard</p>
        <h1 className="mt-5 font-display text-5xl font-light text-white sm:text-6xl">Your originals.</h1>
        <p className="mt-4 text-sm text-zinc-500">Signed in as {user.email}</p>
        <div className="mt-12 space-y-6">
          {fetching && <p data-testid="downloads-loading-state" className="text-zinc-500">Loading downloads...</p>}
          {!fetching && orders.length === 0 && (
            <div data-testid="downloads-empty-state" className="border border-white/10 bg-carbon p-10 text-center">
              <p className="font-display text-3xl text-white">No purchased downloads yet.</p>
              <p className="mt-3 text-sm text-zinc-500">Browse the event library and collect a frame or complete album.</p>
              <Link data-testid="dashboard-browse-events-button" to="/events" className="mt-6 inline-flex border border-gold/40 px-6 py-3 font-mono text-xs uppercase tracking-[0.22em] text-gold hover:bg-gold hover:text-onyx">Browse events</Link>
            </div>
          )}
          {orders.map(({ order, downloads }) => (
            <section key={order.id} data-testid={`download-order-${order.id}`} className="border border-white/10 bg-carbon p-6">
              <div className="mb-5 flex flex-col justify-between gap-2 border-b border-white/10 pb-4 sm:flex-row">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-zinc-500">Order {order.id.slice(0, 8)}</p>
                  <h2 className="mt-2 font-display text-2xl text-white">${Number(order.total).toFixed(2)} · {new Date(order.created_at).toLocaleDateString()}</h2>
                </div>
                <span className="font-mono text-xs uppercase tracking-[0.22em] text-gold">{order.payment_status}</span>
                <button data-testid={`resend-email-${order.id}`} onClick={() => resendEmail(order.id)} className="border border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-300 transition-colors hover:border-gold/50 hover:text-gold">Resend email</button>
              </div>
              <div className="grid gap-3">
                {downloads.map((download) => (
                  <div key={download.download_url} className="flex flex-col justify-between gap-4 border border-white/10 p-4 sm:flex-row sm:items-center">
                    <div>
                      <p className="font-lux text-white">{download.title}</p>
                      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Expires {new Date(download.expires_at).toLocaleDateString()} · {download.download_count}/{download.max_downloads} used</p>
                    </div>
                    <a data-testid={`download-file-${download.item_type}`} href={`${process.env.REACT_APP_BACKEND_URL}${download.download_url}`} className="inline-flex items-center justify-center gap-2 bg-gold px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] text-onyx hover:bg-titanium">
                      <Download className="h-4 w-4" /> Download
                    </a>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
